import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { App } from './App.js';
import { networkMonitor } from './net-indicator.js';
import type { PageDetail } from './content/details.js';
import './styles.css';

/**
 * Client entry — plan §12.4.
 *
 * Content pages are prerendered to static HTML, so this HYDRATES rather than
 * renders when that markup is present. Rendering over it would throw the
 * prerendered content away on first paint, which is the whole SEO and LCP
 * benefit gone.
 */
networkMonitor.start();

/**
 * Register the service worker — plan §12.4, §20.4 (B3).
 *
 * After load, deliberately: registration competes with the page's own critical
 * path, and the landing pages have a 1.2 s LCP budget. Nothing here is needed
 * for the first render.
 *
 * Only in a real build. Under Vite's dev server the module graph is served
 * unbundled from paths that do not exist in production, and a worker caching
 * those would serve yesterday's modules against today's source — the classic
 * way a service worker turns into a day of confusing debugging.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // A refused registration costs offline support and nothing else. Private
      // browsing and some enterprise policies decline it, and the app must
      // work exactly as before when they do.
    });
  });
}

/**
 * The current page's prose, embedded by the prerender.
 *
 * Read from the document rather than imported: `content/details.ts` holds
 * every tool page's copy, and importing it here would put all of it into the
 * bundle for every page — which is what it used to do, at 76% of the entry
 * chunk. Hydration needs this synchronously, so a JSON block in the HTML is
 * the right shape rather than a second request.
 */
function embeddedDetail(): PageDetail | null {
  const node = document.getElementById('__page_detail__');
  if (!node?.textContent) return null;
  try {
    return JSON.parse(node.textContent) as PageDetail;
  } catch {
    // Malformed data must not take the whole page down; the prerendered HTML
    // is already on screen and correct.
    return null;
  }
}

const container = document.getElementById('root');

if (container) {
  const tree = (
    <StrictMode>
      <App detail={embeddedDetail()} />
    </StrictMode>
  );

  // A prerendered page arrives with markup already inside #root.
  if (container.childElementCount > 0) hydrateRoot(container, tree);
  else createRoot(container).render(tree);
}
